import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import AuthShell from "@/pages/auth/AuthShell";
import { PasswordInput } from "@/components/PasswordInput";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError(t("register.mismatch")); return; }
    if (password.length < 8) { setError(t("register.tooShort")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(t("reset.done"));
      nav("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t("reset.title")} subtitle={t("reset.subtitle")} testid="reset-page"
      footer={<Link to="/login" className="font-semibold text-cyan-dark hover:underline">{t("reset.back")}</Link>}>
      {!token ? (
        <div className="rounded-lg bg-red-50 px-3 py-3 text-sm text-red-600" data-testid="reset-invalid">{t("reset.invalid")}</div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-2"><Label>{t("reset.newPass")}</Label><PasswordInput required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" /></div>
          <div className="space-y-2"><Label>{t("reset.confirm")}</Label><PasswordInput required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" /></div>
          {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>}
          <Button type="submit" disabled={loading} data-testid="reset-submit-button" className="w-full rounded-full bg-navy text-white hover:bg-navy-muted">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("reset.updating")}</> : t("reset.submit")}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
